import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SlidersHorizontal } from 'lucide-react';
import { ActionBar, Container, PageHeader, Section } from '@vnetwork/ui/layout';
import { Button, Badge } from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { useSubmitLmsPresetProfiles } from '@/api/media/live-presets';
import type { CreateLmsTranscodingPresetRequest } from '@/api/media/live-presets-types';
import { presetProfiles } from '@/pages/services/shared/transcoding-preset-constants';
import type { PresetProfile } from '@/pages/services/shared/transcoding-preset-constants';
import { PresetProfileCard } from '@/pages/services/shared/PresetProfileCard';
import styles from './PresetSelect.module.css';

/**
 * After submitting, returns to the Create Event Job page if `returnTo` query-param
 * is present (set by the event-job create flow when no presets exist). Otherwise
 * navigates back to the presets list.
 *
 * Query params:
 *   returnTo  — URL-encoded path to return to after successful submission.
 */
export interface LivePresetSelectProps {
  serviceId: string;
}

const toPayload = (profile: PresetProfile): CreateLmsTranscodingPresetRequest => ({
  name: profile.name,
  description: profile.description,
  video: {
    codec: profile.params.video.codec,
    bitrate: profile.params.video.bitrateKbps,
    fps: profile.params.video.fps,
    gop: profile.params.video.gop,
    width: profile.params.video.dimension.width,
    height: profile.params.video.dimension.height,
    profile: profile.params.video.profile,
  },
  audio: {
    codec: profile.params.audio.codec,
    bitrate: profile.params.audio.bitrateKbps,
    sample_rate: profile.params.audio.sampleRate,
    channels: 2,
  },
});

export function LivePresetSelect({ serviceId }: LivePresetSelectProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const { show } = useToast();

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [selectionError, setSelectionError] = useState('');

  const submitMut = useSubmitLmsPresetProfiles(serviceId, {
    onSuccess: () => {
      show({ tone: 'success', title: 'Preset profiles submitted' });
      navigate(returnTo ?? '..', { relative: returnTo ? undefined : 'path' });
    },
    onError: (e) => show({ tone: 'danger', title: e.message || 'Failed to submit preset profiles' }),
  });

  const toggle = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setSelectionError('');
  };

  const handleSubmit = () => {
    if (selectedIds.size === 0) { setSelectionError('Select at least one preset.'); return; }
    const payload = presetProfiles.filter((p) => selectedIds.has(p.id)).map(toPayload);
    submitMut.mutate(payload);
  };

  return (
    <Container size="lg">
      <PageHeader
        icon={<SlidersHorizontal size={22} />}
        title="Select Transcoding Presets"
        description="Pick one or more predefined transcoding presets to add to this service."
      />

      <Section
        title="Select Presets"
        description="Choose one or more predefined transcoding presets to submit."
        actions={selectedIds.size > 0 ? <Badge tone="info">{selectedIds.size} selected</Badge> : undefined}
      >
        <div className={styles.grid}>
          {presetProfiles.map((profile) => (
            <PresetProfileCard
              key={profile.id}
              profile={profile}
              selected={selectedIds.has(profile.id)}
              onToggle={() => toggle(profile.id)}
            />
          ))}
        </div>

        {selectionError && (
          <Alert tone="danger" className={styles.selectionError}>{selectionError}</Alert>
        )}
      </Section>

      <ActionBar
        start={<Button variant="secondary" onClick={() => navigate('..', { relative: 'path' })} disabled={submitMut.isPending}>Cancel</Button>}
        end={<Button onClick={handleSubmit} disabled={submitMut.isPending}>{submitMut.isPending ? 'Submitting…' : 'Submit Presets'}</Button>}
      />
    </Container>
  );
}
